import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import EntitySelector from "../../components/EntitySelector/EntitySelector";
import { CoownerData, Step, StepThreePF } from "../../types";
import { asDni, asHighlighted } from "../../utils";
import {
  confirmEmailRequired,
  isDniExcludedFrom,
  isEmailExcludedFrom,
  smallStringRequired,
} from "../../validations";
import { onUpdateFormData } from "../../reducers/onboardingDataReducer";
import {
  capitalizeString,
  formatDNI,
  goToStep,
  handleSendStep,
} from "../../app/utils";
import { onUpdateActiveIndex } from "../../reducers/collectionReducer";
import { FormikValues } from "formik";

const coownersStep = ({
  disableAllFields,
  isFirstCoowner,
}: {
  disableAllFields?: boolean;
  isFirstCoowner?: boolean;
}): Omit<Step, "step"> => {
  const emptyCoowner: CoownerData = {
    name: "",
    lastName: "",
    email: "",
    idNumber: "",
    confirmEmail: "",
  };

  const isCurrentCoowner = (
    formValues: FormikValues | null,
    name: string,
    lastName: string,
  ) => name === formValues?.name && lastName === formValues?.lastName;

  const isCompletedCoowner = (coowner: CoownerData) =>
    coowner && Object.values(coowner).every((value) => value);

  return {
    name: "Cotitularidad",
    titleOfStep: "Cotitularidad",
    subtitle: isFirstCoowner
      ? "Ingresá los datos de tus cotitulares"
      : "Verificá los datos de tus cotitulares",
    component: (dispatch, state, formik) => {
      if (!state) throw new Error("Error: state not found");
      const { formValues, sendedSteps } = state.onboardingData;
      const coowners: CoownerData[] = formValues?.coowners;

      const { step } = state.onboardingData.stepData;

      return (
        <EntitySelector
          formik={formik}
          entityType="coowner"
          unsaveChangesAlert={{ title: "¿Descartar cotitular?" }}
          entities={
            coowners
              ? (coowners as CoownerData[]).map(({ name, lastName }, index) => ({
                  label: `Cotitular ${index + 1}`,
                  value:
                    name && lastName
                      ? `${name} ${lastName} ${
                          isCurrentCoowner(formValues, name, lastName)
                            ? "(Tú)"
                            : ""
                        }`
                      : "",
                  placeholder: "Nombre Apellido",
                }))
              : []
          }
          handleEntities={() => {
            if (!coowners || coowners.length === 0) {
              const firstCoowner: CoownerData = {
                name: formValues?.name,
                lastName: capitalizeString(formValues?.lastName),
                email: formValues?.email,
                idNumber: formValues?.identityDocument,
                confirmEmail: formValues?.email,
              };

              const newCoowners = [firstCoowner, emptyCoowner];
              dispatch(onUpdateActiveIndex(2));
              dispatch(onUpdateFormData({ coowners: newCoowners }));
            }

            // Ensure there is always a second coowner slot available
            if (coowners && coowners.length === 1) {
              const newCoowners = [...coowners, emptyCoowner];
              dispatch(onUpdateFormData({ coowners: newCoowners }));
            }
          }}
          isCompletedEntity={(i) => isCompletedCoowner(coowners[i])}
          addEntityButton={
            isFirstCoowner
              ? {
                  text: `Agregar cotitular ${(coowners?.length ?? 0) + 1}`,
                  onClick: () => {
                    const newCoowners = [...coowners, emptyCoowner];
                    dispatch(onUpdateFormData({ coowners: newCoowners }));
                  },
                  disabled:
                    !coowners ||
                    coowners?.length === 3 ||
                    !coowners?.every(isCompletedCoowner),
                }
              : undefined
          }
          infoAlert={
            isFirstCoowner
              ? {
                title: "Agregá hasta 3 personas.",
                  description: "Cada persona recibirá un mail para finalizar el proceso de apertura de la cuenta. Recordá: Cada mail que ingreses se vinculará permanentemente al firmante correspondiente. Aconsejamos que sean correos personales y con acceso directo, ya que no podrán desvincularse.",
                  hasSummary: true,
                }
              : undefined
          }
          disabledFirst={isFirstCoowner}
          onContinueButton={{
            onClick: async () => {
              const filteredCoowners = coowners.filter(isCompletedCoowner);
              const additionalCoowners = filteredCoowners
                .filter((coowner) => coowner.email !== formValues?.email) // Exclude primary owner
                .map(({ name, lastName, email, idNumber }) => ({
                  name,
                  lastName,
                  email,
                  idNumber: formatDNI(idNumber || ""),
                }));

              const stepThree: StepThreePF = {
                "@type": "3_PHYSICAL",
                coOwners: additionalCoowners,
              };

              if (!disableAllFields) {
                await handleSendStep({
                  dispatch,
                  personType: "PHYSICAL",
                  email: formValues?.email || "",
                  sendedSteps,
                  stepId: 3,
                  step: stepThree,
                });
              }

              goToStep({ dispatch, step: Number(step) + 1 });
            },
            disabled:
              !coowners ||
              coowners.length === 1 ||
              !coowners?.every(isCompletedCoowner),
          }}
        />
      );
    },
    insideForm: true,
    secondaryForm: {
      title: (activeIndex) =>
        `Datos del cotitular ${activeIndex} ${
          activeIndex === 3 ? "(Opcional)" : ""
        }`,
      mobileTitle: "Cargar cotitular",
      collectionName: "coowners",
      deleteEntity: {
        alert: {
          title: ({ name, lastName }) => (
            <>
              ¿Eliminar a{" "}
              {asHighlighted([name, lastName].filter(Boolean).join(" "))} como
              cotitular?
            </>
          ),
        },
        // The account holder is always the first coowner, and coowners can only
        // be removed while they are being loaded for the first time
        hidden: (state) =>
          state.collectionData.activeIndex === 1 || !isFirstCoowner,
        onClick: (dispatch, state, setPrimaryFormValues) => {
          const { activeIndex } = state.collectionData;
          if (!activeIndex) return;

          const coowners: CoownerData[] =
            state.onboardingData.formValues?.coowners || [];
          const index = activeIndex - 1;
          const newCoowners = coowners.filter((_, i) => i !== index);

          dispatch(onUpdateFormData({ coowners: newCoowners }));
          if (index !== 1) dispatch(onUpdateActiveIndex(index));

          setPrimaryFormValues?.((prevValues) => ({
            ...prevValues,
            coowners: newCoowners,
          }));
        },
      },
      fields: [
        {
          name: "name",
          type: "text",
          label: "Nombre/s",
          validation: smallStringRequired,
          initialValue: "",
          columns: 2,
          disabled: disableAllFields,
        },
        {
          name: "lastName",
          type: "text",
          label: "Apellido/s",
          validation: smallStringRequired,
          initialValue: "",
          columns: 2,
          disabled: disableAllFields,
        },
        {
          name: "idNumber",
          type: "dni",
          label: "DNI",
          initialValue: "",
          conditionalValidation: (state) => {
            if (!state) throw new Error("Error: state not found");
            const activeIndex = state.collectionData.activeIndex;

            const { formValues } = state.onboardingData;
            const coowners: CoownerData[] = formValues?.coowners || [];
            const coownersDni = coowners.map(({ idNumber }) => idNumber || "");
            const dnisExcludingActiveIndex = coownersDni.filter(
              (_, index) => activeIndex !== null && index !== activeIndex - 1,
            );

            return isDniExcludedFrom(dnisExcludingActiveIndex, "cotitular");
          },
          formatInPreConfirmation: (value) =>
            value ? asDni(value).replace(/^0/, "") : "",
          disabled: disableAllFields,
        },
        {
          type: "email",
          name: "email",
          label: "Mail",
          conditionalValidation: (state) => {
            if (!state) throw new Error("Error: state not found");
            const activeIndex = state.collectionData.activeIndex;

            const { formValues } = state.onboardingData;
            const coowners: CoownerData[] = formValues?.coowners || [];
            const coownersEmail = coowners.map(({ email }) => email);
            const emailsExcludingActiveIndex = coownersEmail.filter(
              (_, index) => activeIndex !== null && index !== activeIndex - 1,
            );

            return isEmailExcludedFrom(emailsExcludingActiveIndex, "cotitular");
          },
          initialValue: "",
          disabled: disableAllFields,
        },
        {
          name: "confirmEmail",
          type: "email",
          label: "Confirmar mail",
          validation: confirmEmailRequired,
          initialValue: "",
          onBlur: async (values, _, setFieldError, __, ___, state) => {
            if (!state) throw new Error("Error: state not found");

            const { formValues } = state.onboardingData;
            const coowners: CoownerData[] = formValues?.coowners || [];
            const coownersEmail = coowners.map(({ email }) => email);
            const value = values.email;

            if (value && coownersEmail.includes(value) && setFieldError) {
              setFieldError(
                "confirmEmail",
                "No se puede utilizar el mismo mail para más de un cotitular.",
              );
            }
          },
          onChange: async (_values, setFieldValue, _, __, element) => {
            // To set error if the email is already in the list of coowners
            const value = element && element.target.value;
            await setFieldValue("confirmEmail", value);
          },
          hideField: !isFirstCoowner,
          disabled: disableAllFields,
          disablePaste: true,
        },
      ],
      onSave: async (values, dispatch, state, setPrimaryFormValues, formik) => {
        if (!state || !dispatch || !formik)
          throw new Error("Error: state or dispatch not found");

        const {
          onboardingData: { formValues },
          collectionData: { activeIndex },
        } = state;

        if (!activeIndex) throw new Error("Error: activeIndex not found");

        const coowners = formValues?.coowners || [];

        const newCoowners = [...coowners];
        newCoowners[activeIndex - 1] = values;

        setPrimaryFormValues &&
          setPrimaryFormValues((prevValues) => ({
            ...prevValues,
            coowners: newCoowners,
          }));
        dispatch(onUpdateFormData({ coowners: newCoowners }));
        showToastMessage("El cotitular fue guardado exitosamente.", {
          containerId: "layoutPage",
          type: "SUCCESS",
        });

        if (
          newCoowners &&
          newCoowners?.length === 3 &&
          newCoowners?.every(isCompletedCoowner)
        ) {
          dispatch(onUpdateActiveIndex(null));
        }
      },
      onSuccessCondition: (state) => {
        if (!state) throw new Error("Error: state not found");

        const { formValues } = state.onboardingData;
        const { activeIndex } = state.collectionData;

        const coowners: CoownerData[] = formValues?.coowners || [];
        return (
          activeIndex === null &&
          coowners.length === 3 &&
          coowners.every(isCompletedCoowner)
        );
      },
      onSuccessContent: {
        title: "Cotitulares guardados",
        subtitle: "¡Listo! Llegaste al máximo de cotitulares.",
        message:
          "Podés editar la información si necesitás hacer cambios o continuar con el registro.",
      },
    },
  };
};

export default coownersStep;
